import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation, withRouter } from 'react-router-dom';
import { Menu } from 'antd';
import menu, { getFilteredMenu } from '../menu';
import { mergeContentExtraMenuPaths } from '../menu/extraMenuPaths';
import rolesApi from '../api/roles';
import usersApi from '../api/users';

function AppSidebar() {
  const isMount = useRef(false);
  const [menuDisplay, setMenuDisplay] = useState([]);
  const authUser = useSelector((state) => state.auth.authUser);
  const history = useHistory();
  const location = useLocation();
  const { pathname } = location;

  useEffect(() => {
    isMount.current = true;

    const getAuthUserId = (user) => user?.id || user?.user?.id || null;

    const normalizePaths = (rawPath) => {
      const normalizeRoutePath = (path) => {
        if (!path) return '';
        let normalized = String(path).trim();
        if (!normalized) return '';
        if (!normalized.startsWith('/')) normalized = `/${normalized}`;
        if (!normalized.startsWith('/app/')) {
          normalized = normalized === '/app' ? '/app' : `/app${normalized}`;
        }
        return normalized.replace(/\/+$/, '');
      };

      if (!rawPath) return [];

      if (Array.isArray(rawPath)) {
        return rawPath
          .filter(Boolean)
          .map(normalizeRoutePath)
          .filter(Boolean);
      }

      if (typeof rawPath === 'string') {
        const trimmed = rawPath.trim();
        if (!trimmed) return [];

        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) {
            return parsed
              .filter(Boolean)
              .map(normalizeRoutePath)
              .filter(Boolean);
          }
        } catch (e) {
          // raw path is not JSON, treat as plain path
        }

        return [normalizeRoutePath(trimmed)].filter(Boolean);
      }

      return [];
    };

    const collectAllowedPathsFromTree = (nodes = []) => {
      const paths = [];
      const traverse = (items = []) => {
        items.forEach((item) => {
          const candidatePaths = [
            item?.path,
            item?.menuitem?.path,
            item?.menu_item?.path,
            item?.menu?.path,
            item?.menuItem?.path,
          ];
          candidatePaths.forEach((candidate) => {
            paths.push(...normalizePaths(candidate));
          });
          if (Array.isArray(item?.children) && item.children.length > 0) {
            traverse(item.children);
          }
        });
      };
      traverse(nodes);
      return [...new Set(paths)];
    };

    const getAdminDashboardRoleId = (user) => {
      const roles = Array.isArray(user?.roles)
        ? user.roles
        : Array.isArray(user?.user?.roles)
          ? user.user.roles
          : [];
      const adminRole = roles.find(
        (r) =>
          r?.Role?.app?.name === 'Admin dashboard' ||
          r?.app?.name === 'Admin dashboard'
      );
      return (
        adminRole?.RoleID ||
        adminRole?.Role?.id ||
        adminRole?.role_id ||
        adminRole?.id ||
        null
      );
    };

    const fetchMenuData = async () => {
      try {
        if (!isMount.current) return;

        // For admin and Super users, show all menu items
        const authRole = authUser?.role || authUser?.user?.role;
        if (authRole === 'admin' || authRole === 'Super') {
          if (authRole === 'Super') {
            setMenuDisplay(menu);
          } else {
            // Hide Super-only Admin 2FA page from non-Super "admin" accounts
            const stripAdmin2FA = (items = []) =>
              items
                .map((item) => {
                  if (item.key === '/app/admin-2fa') return null;
                  if (!item.children) return item;
                  return { ...item, children: stripAdmin2FA(item.children) };
                })
                .filter(Boolean);
            setMenuDisplay(stripAdmin2FA(menu));
          }
          return;
        }

        // Prefer explicit user menu assignment first
        const authUserId = getAuthUserId(authUser);
        if (!authUserId) {
          setMenuDisplay(getFilteredMenu());
          return;
        }
        const userMenusRes = await usersApi.getUserMenus(authUserId);
        const userMenus =
          userMenusRes?.data?.menus ||
          userMenusRes?.data?.data?.menus ||
          userMenusRes?.data?.data ||
          [];
        let allowedPaths = collectAllowedPathsFromTree(userMenus);

        // If user has no explicit menu assignment, fallback to role menu tree
        if (allowedPaths.length === 0) {
          const roleId = getAdminDashboardRoleId(authUser);
          if (roleId) {
            const roleMenusRes = await rolesApi.getRoleMenusCached(roleId);
            const roleMenus =
              roleMenusRes?.data?.menus ||
              roleMenusRes?.data?.data?.menus ||
              roleMenusRes?.data?.data ||
              [];
            allowedPaths = collectAllowedPathsFromTree(roleMenus);
          }
        }

        const filteredMenu = filterMenu(menu, mergeContentExtraMenuPaths(allowedPaths));
        setMenuDisplay(filteredMenu);
      } catch (error) {
        console.error('Error fetching menu data:', error);
        // On error, show filtered menu from localStorage
        setMenuDisplay(getFilteredMenu());
      }
    };

    fetchMenuData();

    return () => {
      isMount.current = false;
    };
  }, [authUser]);

  const onClickMenu = (key) => {
    history.push(key);
  };

  const filterMenu = (menuItems, allowedPaths) => {
    return menuItems
      .map((menuItem) => {
        const hasChildren = menuItem.children && menuItem.children.length > 0;
        let filteredChildren = null;

        if (hasChildren) {
          filteredChildren = filterMenu(menuItem.children, allowedPaths);
        }

        const hasMatchingChild =
          filteredChildren && filteredChildren.length > 0;
        const hasMatchingItem = Array.isArray(allowedPaths)
          ? allowedPaths.includes(menuItem.key)
          : false;

        if (hasMatchingChild) {
          return { ...menuItem, children: filteredChildren };
        } else if (hasMatchingItem) {
          return menuItem;
        }

        return null;
      })
      .filter(Boolean);
  };

  return (
    <Menu
      theme="light"
      mode="inline"
      selectedKeys={[pathname]}
      style={{ borderRight: 0 }}
      onClick={({ key }) => onClickMenu(key)}
      items={menuDisplay}
    />
  );
}

export default withRouter(AppSidebar);